import axios, { CancelTokenSource } from "axios";
import { NAVIGATION_PAGE_SIZE } from "consts";
import {
  CorrectionRawResponse,
  CorrectionsFormContent,
  CreateReceiptFormValues,
  DataGridRequestParams,
  DispositionFormMaterial,
  EditDispositionParams,
  Transaction,
  TransactionMaterial,
  TransactionSection,
  TransactionType,
} from "fe-shared-transactions/src/types";
import { RecordsListItem } from "fe-shared/src/types/common";
import { TransactionRecordsListItem } from "fe-shared/src/types/common/records";
import transactionPhotosConverter from "fe-shared-transactions/src/helpers/transactionPhotosConverter";
import isTransactionWithWeightAndScale from "fe-shared-transactions/src/helpers/transactions";
import waitForBatchToBeFinished from "fe-shared-transactions/src/helpers/waitForBatchToBeFinished";
import { retreadingTransactionFormValuesApiMapper } from "../../../helpers/retreadingTransactionFormValuesApiMapper";
import { RetreadingTransactionFormContent } from "../../../types";

export interface AddNoteParams {
  text: string;
  transactionId: number;
}

export interface ActionParams {
  transactionId: number;
  reason?: string;
}

export interface ActionUnFlagParams {
  transactionId: number;
  sectionCode: TransactionSection;
}

export interface ActionFlagParams extends ActionUnFlagParams {
  text: string;
  isVisibleToParticipant: boolean;
}

export interface InvalidationParams {
  transactionId: number;
  reason: string;
  showToParticipant: boolean;
  isAdjustment?: boolean;
}

export interface RestoreParams {
  transactionId: number;
  isAdjustment?: boolean;
}

export interface NavigationTransaction {
  transactionId: number;
  transactionNumber: string;
}

export interface NavigationTransactions {
  total: number;
  transactionsIds: NavigationTransaction[];
}

export interface TransactionListInfo {
  isPresent: boolean;
  currentTransactionIndex: number;
}

export interface CreateAdjustmentParams {
  transactionId: number;
  content: CorrectionsFormContent;
}

export interface CreateAdjustmentResponse {
  adjustmentNumber: string;
}

export interface GetLinkedTransactions {
  transactionNumber: string;
  isTargetTransactionNext?: boolean;
}

export const getTransaction = (
  apiBaseUrl: string | undefined,
  transactionNumber: string,
  cancelToken: CancelTokenSource
): Promise<any> => 
  axios
  .get("transactions", {
    params: { transactionNumber },
    cancelToken: cancelToken?.token,
  })
  .then((res) =>
    transactionPhotosConverter(res.data.transaction, apiBaseUrl)
  );


export const addTransactionNote = ({ text, transactionId }: AddNoteParams) =>
  axios
    .post(
      "transactions/notes",
      { text },
      { params: { transactionId }, hideToast: true }
    )
    .then((res) => res.data);

export const getTransactionNotes = (
  transactionId: number
): Promise<RecordsListItem[]> =>
  axios
    .get("transactions/notes", {
      params: { transactionId },
    })
    .then((res) => res.data);

export const flagTransaction = (params: ActionFlagParams) => {
  return axios.post("transactions/flag", params).then((res) => res.data);
};

export const unflagTransaction = (params: ActionUnFlagParams) => {
  return axios.post("transactions/unflag", params).then((res) => res.data);
};

export const invalidateTransaction = ({
  transactionId,
  reason,
  showToParticipant,
  isAdjustment = false,
}: InvalidationParams): Promise<any> =>
  axios
    .post(`${isAdjustment ? "adjustments" : "transactions"}/invalidate`, {
      transactionId,
      invalidationInfo: {
        reason,
        showToParticipant,
      },
    })
    .then((res) => res.data);

export const restoreInvalidatedTransaction = ({
  transactionId,
  isAdjustment = false,
}: RestoreParams): Promise<any> =>
  axios
    .post(`${isAdjustment ? "adjustments" : "transactions"}/restore`, {
      transactionId,
    })
    .then((res) => res.data);

export const returnTransaction = ({
  transactionId,
}: RestoreParams): Promise<any> =>
  axios.post("transactions/return", { transactionId }).then((res) => res.data);

function isDeliveryToProcessor(transaction: Transaction): boolean {
  const { transactionType } = transaction;

  return (
    isTransactionWithWeightAndScale(transaction) &&
    transactionType !== TransactionType.Adjustment
  );
}

function fetchTransactionNumber(transaction: Transaction): string | undefined {
  const { transactionType, transactionNumber, linkedTransactions } =
    transaction;

  return transactionType === TransactionType.Delivery
    ? transactionNumber
    : linkedTransactions.find(
        (lt) => lt.transactionType === TransactionType.Delivery
      )?.transactionNumber;
}

async function addToMaterialTraceabilityHistory(
  transactionNumber: string | undefined
): Promise<{ isTraverseTriggered: boolean }> {
  return axios
    .post("materialTraceabilityHistory", {
      transactionNumbers: [transactionNumber],
    })
    .then((res) => res.data);
}

async function getUnidentifiedRoutes(
  transactionNumber: string | undefined
): Promise<TransactionMaterial[]> {
  const { data: materials } = await axios.get<TransactionMaterial[]>(
    "transactions/getUnidentifiedRoutes",
    { params: { transactionNumber } }
  );

  return materials;
}

export const approveTransaction = async (
  transaction: Transaction
): Promise<TransactionMaterial[]> => {
  const { transactionId } = transaction;

  if (isDeliveryToProcessor(transaction)) {
    const transactionNumber = fetchTransactionNumber(transaction);

    const { isTraverseTriggered } = await addToMaterialTraceabilityHistory(
      transactionNumber
    );

    if (isTraverseTriggered) {
      const batchCheckTimestamp = new Date(
        transaction.completedDate || Date.now()
      ).getTime();

      await waitForBatchToBeFinished(transactionId, batchCheckTimestamp);
    }

    const materials = await getUnidentifiedRoutes(transactionNumber);

    if (materials.length) {
      return materials;
    }
  }

  await axios.post("transactions/approve", {
    transactionId,
  });

  return [];
};

export const getNavigationTransactions = (
  queryKey: string,
  requestParams: DataGridRequestParams,
  page: number | null = 1
) =>
  axios
    .post<NavigationTransactions>("getTransactionsIds", {
      ...requestParams,
      ...(page !== null
        ? {
            limit: NAVIGATION_PAGE_SIZE,
            skip: NAVIGATION_PAGE_SIZE * (page - 1),
          }
        : {}),
    })
    .then((res) => res.data);

export const getTransactionTransactionListInfo = (
  transactionNumber: string,
  requestParams: DataGridRequestParams,
  cancelToken: CancelTokenSource
) =>
  axios
    .post<TransactionListInfo>(
      "getTransactionListInfo",
      {
        transactionNumber,
        ...requestParams,
      },
      {
        cancelToken: cancelToken?.token,
      }
    )
    .then((res) => res.data);

export const getCorrections = (
  transactionNumber: string,
  cancelToken: CancelTokenSource
): Promise<CorrectionRawResponse> =>
  axios
    .get("corrections", {
      params: { transactionNumber },
      cancelToken: cancelToken?.token,
    })
    .then((res) => res.data);

const adjustmentFormContentMapper = (content: CorrectionsFormContent) => ({
  ...content,
  scaleTickets: content.scaleTickets?.map((item) => ({
    ticketId: item.ticketId,
    ticketNumber: item.correctedTicketNumber,
  })),
});

export const createAdjustment = ({
  transactionId,
  content,
}: CreateAdjustmentParams): Promise<CreateAdjustmentResponse> =>
  axios
    .post("adjustments", {
      transactionId,
      ...adjustmentFormContentMapper(content),
    })
    .then((res) => res.data);

export const editAdjustment = ({
  transactionId,
  content,
}: CreateAdjustmentParams) =>
  axios
    .put("adjustments", {
      transactionId,
      ...adjustmentFormContentMapper(content),
    })
    .then((res) => res.data);

export const editDisposition = ({
  materialGroups,
  ...params
}: EditDispositionParams) =>
  axios
    .put("transactions/disposition", {
      ...params,
      materials: materialGroups
        .reduce(
          (result, g) => [...result, ...g.items],
          [] as DispositionFormMaterial[]
        )
        .filter((m) => m.units.unitQuantity > 0),
    })
    .then((res) => res.data);

export const editRetreadingTransaction = (
  values: RetreadingTransactionFormContent
) => {
  const requestBody = retreadingTransactionFormValuesApiMapper(values, true);

  return axios
    .put("transactions/retreader", requestBody)
    .then(({ data }) => data);
};

export const getLinkedTransactions = (
  params: GetLinkedTransactions
): Promise<TransactionRecordsListItem[]> =>
  axios
    .get("linkedTransactions/getLinkedTransactions", { params })
    .then((res) => res.data);

export const cancelDeliveryTransaction = (transactionNumber: string, reason: string): Promise<void> =>
  axios
    .delete("synchronization", { params: { transactionCode: transactionNumber, reason } })
    .then((res) => res.data);

export const createReceipt = (
  transactionId: number,
  values: CreateReceiptFormValues
): Promise<void> =>
  axios
    .post("transactions/createReceipt", {
      transactionId,
      scaleTicketNumber: values.scaleTicketNumber,
      grossWeight: values.grossWeight,
      tareWeight: values.tareWeight,
      comment: values.comment || null,
      scaleTicketFiles: values.scaleTicketFiles.map((f) => f.path),
      tirePhotoFiles: values.tirePhotoFiles.map((f) => f.path),
      attachments: values.attachments.map((f) => f.path),
      targetWarehouseId: values.targetWarehouseId ?? null,
      trailerNumber: values.trailerNumber ?? null,
      actualTransactionDate: values.actualTransactionDate ?? null,
      useActualTransactionDateAsScaleTicketDate:
        values.useActualTransactionDateAsScaleTicketDate,
    })
    .then((res) => res.data);
